import { useEffect, useState } from 'react';

import { getIsCouponActivated, saveCouponInStorage } from 'common/components/Coupon/storage';
import { EventBusEvent } from 'common/enums/EventBus';
import eventBus from 'common/event-bus/EventBus';
import { useSubscription } from 'common/hooks/subscription';
import type { ArticlePriceProps } from './ArticlePrice';

export const useActivatedCoupon = () => {
  const [coupon] = useSubscription<string>(EventBusEvent.ToggleCoupon, { latestEmitted: true });

  return coupon;
};

export const useCoupon = ({
  coupon,
  specialPrice: baseSpecialPrice,
  regularPrice,
  sku,
  onCouponChange,
}: Pick<
  ArticlePriceProps,
  'coupon' | 'specialPrice' | 'regularPrice' | 'onCouponChange' | 'sku'
>) => {
  const priceAfterDiscount = coupon?.priceAfterDiscount || 0;
  const code = coupon?.code;

  const [isActivated, setIsActivated] = useState(false);
  const [specialPrice, setSpecialPrice] = useState(baseSpecialPrice);
  const [showCoupon, setShowCoupon] = useState(false);

  const handleStateChange = (isActive: boolean) => {
    const price = isActive
      ? {
          value: priceAfterDiscount,
          discount: regularPrice.value - priceAfterDiscount,
        }
      : baseSpecialPrice;

    eventBus.emit(EventBusEvent.ToggleCoupon, isActive ? code : null);

    setSpecialPrice(price);
    setIsActivated(isActive);
  };

  useEffect(() => {
    const isActive = coupon?.applied || getIsCouponActivated(sku);
    handleStateChange(isActive);
    setShowCoupon(!!coupon);
  }, []);

  const handleCouponChange = () => {
    const isActive = !isActivated;
    onCouponChange?.(isActive ? coupon?.code : null);
    handleStateChange(isActive);
    saveCouponInStorage(sku, isActive);
  };

  return { showCoupon, specialPrice, isActivated, handleCouponChange };
};
